import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, Building2, Plus, KeyRound, Loader2, Trash2, Smartphone, Save, UserCog, Store, HardHat, Package, QrCode, Pencil, Layers, Tag } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ROLE_LABEL } from "@/lib/format";
import AppVersionPanel from "@/components/AppVersionPanel";
import VendorsPanel from "@/components/VendorsPanel";
import AssetsPanel from "@/components/AssetsPanel";
import OutletQRDialog from "@/components/OutletQRDialog";

export default function Manage() {
  const { user } = useAuth();
  const readOnly = user?.role === "manager" || user?.role === "op_manager";
  const [users, setUsers] = useState([]);
  const [outlets, setOutlets] = useState([]);

  const load = async () => {
    const [u, o] = await Promise.all([api.get("/users"), api.get("/outlets")]);
    setUsers(u.data); setOutlets(o.data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display font-bold text-3xl text-slate-900">Manage</h1>
        <p className="text-sm text-slate-500 mt-1">
          {readOnly ? "Read-only view of users and outlets." : "Add and manage users, outlets, and assignments."}
        </p>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-xl">
          <TabsTrigger value="users" data-testid="tab-users" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 rounded-lg">
            <Users className="w-4 h-4 mr-1.5" /> Users
          </TabsTrigger>
          <TabsTrigger value="outlets" data-testid="tab-outlets" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 rounded-lg">
            <Building2 className="w-4 h-4 mr-1.5" /> Outlets
          </TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 rounded-lg">
            <HardHat className="w-4 h-4 mr-1.5" /> Vendors
          </TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 rounded-lg">
            <Package className="w-4 h-4 mr-1.5" /> Assets
          </TabsTrigger>
          {user?.role === "admin" && (
            <TabsTrigger value="zones" data-testid="tab-zones" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 rounded-lg">
              <Layers className="w-4 h-4 mr-1.5" /> Zones & Categories
            </TabsTrigger>
          )}
          {user?.role === "admin" && (
            <TabsTrigger value="app" data-testid="tab-app" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700 rounded-lg">
              <Smartphone className="w-4 h-4 mr-1.5" /> App
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <UsersPanel users={users} outlets={outlets} readOnly={readOnly} reload={load} currentUser={user} />
        </TabsContent>
        <TabsContent value="outlets" className="mt-4">
          <OutletsPanel outlets={outlets} users={users} readOnly={readOnly} reload={load} currentUser={user} />
        </TabsContent>
        <TabsContent value="vendors" className="mt-4">
          <VendorsPanel readOnly={readOnly} canDelete={user?.role === "admin"} />
        </TabsContent>
        <TabsContent value="assets" className="mt-4">
          <AssetsPanel outlets={outlets} readOnly={readOnly} canDelete={user?.role === "admin"} />
        </TabsContent>
        {user?.role === "admin" && (
          <TabsContent value="zones" className="mt-4">
            <ZonesCategoriesPanel outlets={outlets} />
          </TabsContent>
        )}
        {user?.role === "admin" && (
          <TabsContent value="app" className="mt-4">
            <AppVersionPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function UsersPanel({ users, outlets, readOnly, reload, currentUser }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("outlet_incharge");
  const [temp, setTemp] = useState("");
  const [outletIds, setOutletIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [allotOpen, setAllotOpen] = useState(false);
  const [allotUser, setAllotUser] = useState(null);
  const [allotOutletIds, setAllotOutletIds] = useState([]);
  const [savingAllot, setSavingAllot] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editName, setEditName] = useState("");
  const [editRole, setEditRole] = useState("outlet_incharge");
  const [editActive, setEditActive] = useState(true);
  const [editSaving, setEditSaving] = useState(false);
  const outletMap = Object.fromEntries(outlets.map(o => [o.id, o]));
  const canAllot = currentUser?.role === "admin" || currentUser?.role === "manager" || currentUser?.role === "op_manager";

  // Compute FC → outlets map from outlets.facility_incharge_ids (source of truth for FCs)
  const fcOutletsMap = {};
  outlets.forEach(o => (o.facility_incharge_ids || []).forEach(fcId => {
    if (!fcOutletsMap[fcId]) fcOutletsMap[fcId] = [];
    fcOutletsMap[fcId].push(o);
  }));

  const outletsFor = (u) => {
    if (u.role === "facility_incharge") return fcOutletsMap[u.id] || [];
    return (u.outlet_ids || []).map(id => outletMap[id]).filter(Boolean);
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/users", { name, email, role, temp_password: temp || "TempPass@123", outlet_ids: outletIds });
      // Sync outlet.facility_incharge_ids for FC creations so visibility works immediately
      if (role === "facility_incharge" && outletIds.length > 0) {
        await api.post(`/users/${data.id}/assign-outlets`, { outlet_ids: outletIds });
      }
      toast.success("User created");
      setOpen(false); setName(""); setEmail(""); setTemp(""); setOutletIds([]);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const resetPw = async (u) => {
    const p = prompt(`Set a new temporary password for ${u.name}`);
    if (!p) return;
    try { await api.post(`/users/${u.id}/reset-password`, { new_temp_password: p }); toast.success("Password reset"); }
    catch { toast.error("Failed"); }
  };

  const remove = async (u) => {
    if (!confirm(`Deactivate ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deactivated"); reload(); }
    catch { toast.error("Failed"); }
  };

  const openAllot = (u) => {
    setAllotUser(u);
    setAllotOutletIds(outletsFor(u).map(o => o.id));
    setAllotOpen(true);
  };

  const saveAllot = async (e) => {
    e.preventDefault();
    setSavingAllot(true);
    try {
      await api.post(`/users/${allotUser.id}/assign-outlets`, { outlet_ids: allotOutletIds });
      toast.success(`Outlets updated for ${allotUser.name}`);
      setAllotOpen(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSavingAllot(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200">
      <div className="flex items-center justify-between p-4 border-b border-slate-200">
        <div className="text-sm font-semibold text-slate-800">{users.length} user{users.length !== 1 ? "s" : ""}</div>
        {!readOnly && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white h-9">
                <Plus className="w-4 h-4 mr-1.5" /> Add user
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>New user</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div><Label>Name</Label><Input data-testid="new-user-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label>Email</Label><Input data-testid="new-user-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" /></div>
                <div>
                  <Label>Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger data-testid="new-user-role" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Temporary password</Label>
                  <Input data-testid="new-user-temp" value={temp} onChange={(e) => setTemp(e.target.value)} className="mt-1.5 h-11" placeholder="Default: TempPass@123" />
                  <p className="text-[11px] text-slate-500 mt-1">User will be asked to change this on first login.</p>
                </div>
                {(role === "facility_incharge" || role === "outlet_incharge" || role === "outlet_staff" || role === "hod") && (
                  <div>
                    <Label>Assign outlets</Label>
                    <div className="mt-1.5 max-h-40 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1">
                      {outlets.map((o) => (
                        <label key={o.id} className="flex items-center gap-2 py-1 text-sm text-slate-700">
                          <input type="checkbox" checked={outletIds.includes(o.id)}
                            onChange={(e) => setOutletIds(e.target.checked ? [...outletIds, o.id] : outletIds.filter((x) => x !== o.id))} />
                          {o.code} · {o.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="new-user-submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create user"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <ul className="divide-y divide-slate-100">
        {users.map((u) => {
          const uOutlets = outletsFor(u);
          const canAllotHere = canAllot && (u.role === "facility_incharge" || u.role === "outlet_incharge" || u.role === "outlet_staff" || u.role === "hod");
          return (
            <li key={u.id} data-testid={`user-row-${u.id}`} className="p-4 flex flex-wrap items-center gap-3 justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900 text-sm">{u.name}</span>
                  {u.role === "facility_incharge" && uOutlets.length > 1 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 text-[10px] font-semibold">
                      <Store className="w-3 h-3" /> {uOutlets.length} outlets
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">{u.email} · {ROLE_LABEL[u.role]}</div>
                {uOutlets.length > 0 && (
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Outlets: {uOutlets.map(o => o.code).join(", ")}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                {canAllotHere && (
                  <Button variant="outline" size="sm" onClick={() => openAllot(u)} data-testid={`allot-outlets-${u.id}`}>
                    <Store className="w-3.5 h-3.5 mr-1" /> Allot outlets
                  </Button>
                )}
                {!readOnly && u.id !== currentUser?.id && (
                  <>
                    {currentUser?.role === "admin" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setEditUser(u); setEditName(u.name); setEditRole(u.role); setEditActive(u.is_active !== false); setEditOpen(true); }}
                        data-testid={`edit-user-${u.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => resetPw(u)} data-testid={`reset-pw-${u.id}`}>
                      <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(u)} data-testid={`del-user-${u.id}`} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit user</DialogTitle></DialogHeader>
          {editUser && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setEditSaving(true);
                try {
                  await api.put(`/users/${editUser.id}`, {
                    name: editName.trim(),
                    role: editRole,
                    is_active: editActive,
                  });
                  toast.success("User updated");
                  setEditOpen(false);
                  reload();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Failed to save");
                } finally { setEditSaving(false); }
              }}
              className="space-y-3"
            >
              <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-xs text-slate-600">
                <span className="text-slate-500">Email:</span> <span className="font-semibold text-slate-900">{editUser.email}</span>
              </div>
              <div>
                <Label>Name</Label>
                <Input data-testid="edit-user-name" required value={editName} onChange={(e) => setEditName(e.target.value)} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={editRole} onValueChange={setEditRole}>
                  <SelectTrigger data-testid="edit-user-role" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" data-testid="edit-user-active" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />
                <span className="text-sm text-slate-700">Active — inactive users won't appear on rosters or shift lists</span>
              </label>
              <DialogFooter>
                <Button type="submit" disabled={editSaving} data-testid="edit-user-submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={allotOpen} onOpenChange={setAllotOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Allot outlets</DialogTitle></DialogHeader>
          {allotUser && (
            <form onSubmit={saveAllot} className="space-y-3">
              <div className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">{allotUser.name}</span>
                <div className="text-xs text-slate-500 mt-0.5">
                  Pick every outlet this {ROLE_LABEL[allotUser.role]} is responsible for. One person can handle many outlets.
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1">
                {outlets.length === 0 && <div className="text-xs text-slate-500 p-2">No outlets yet.</div>}
                {outlets.map((o) => (
                  <label key={o.id} className="flex items-center gap-2 py-1.5 px-2 text-sm text-slate-700 hover:bg-slate-50 rounded-md cursor-pointer">
                    <input type="checkbox" data-testid={`allot-outlet-check-${o.id}`}
                      checked={allotOutletIds.includes(o.id)}
                      onChange={(e) => setAllotOutletIds(
                        e.target.checked ? [...allotOutletIds, o.id] : allotOutletIds.filter(x => x !== o.id)
                      )} />
                    <span className="flex-1">
                      <span className="font-medium text-slate-900">{o.code}</span>
                      <span className="text-slate-500"> · {o.name}</span>
                    </span>
                  </label>
                ))}
              </div>
              <div className="text-[11px] text-slate-500">
                Currently selected: <span className="font-semibold text-slate-800">{allotOutletIds.length}</span>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={savingAllot} data-testid="allot-outlets-save" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  {savingAllot ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save assignments"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OutletsPanel({ outlets, users, readOnly, reload, currentUser }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [address, setAddress] = useState("");
  const [fcIds, setFcIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editFcIds, setEditFcIds] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [qrOutlet, setQrOutlet] = useState(null);
  const facilityIncharges = users.filter(u => u.role === "facility_incharge");
  const userMap = Object.fromEntries(users.map(u => [u.id, u]));
  const canAdd = currentUser?.role === "admin";
  const canAssign = currentUser?.role === "admin" || currentUser?.role === "manager" || currentUser?.role === "op_manager";

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/outlets", { name, code, address, facility_incharge_ids: fcIds });
      toast.success("Outlet added");
      setOpen(false); setName(""); setCode(""); setAddress(""); setFcIds([]);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const remove = async (o) => {
    if (!confirm(`Delete outlet ${o.name}?`)) return;
    try { await api.delete(`/outlets/${o.id}`); toast.success("Deleted"); reload(); }
    catch { toast.error("Failed"); }
  };

  const openEdit = (o) => {
    setEditing(o);
    setEditFcIds(o.facility_incharge_ids || []);
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await api.post(`/outlets/${editing.id}/assign-fcs`, { facility_incharge_ids: editFcIds });
      toast.success(`${editing.name} assignments updated`);
      setEditOpen(false); reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSavingEdit(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200">
      <div className="flex items-center justify-between p-4 border-b border-slate-200">
        <div className="text-sm font-semibold text-slate-800">{outlets.length} outlet{outlets.length !== 1 ? "s" : ""}</div>
        {canAdd && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-outlet-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white h-9">
                <Plus className="w-4 h-4 mr-1.5" /> Add outlet
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>New outlet</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div><Label>Name</Label><Input data-testid="new-outlet-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11" /></div>
                <div><Label>Code</Label><Input data-testid="new-outlet-code" required value={code} onChange={(e) => setCode(e.target.value)} className="mt-1.5 h-11" placeholder="e.g. BAN04" /></div>
                <div><Label>Address</Label><Input data-testid="new-outlet-address" value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1.5 h-11" /></div>
                <div>
                  <Label>Facility Incharges</Label>
                  <div className="mt-1.5 max-h-40 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1">
                    {facilityIncharges.length === 0 && <div className="text-xs text-slate-500">No FCs yet. Add one on the Users tab.</div>}
                    {facilityIncharges.map((u) => (
                      <label key={u.id} className="flex items-center gap-2 py-1 text-sm text-slate-700">
                        <input type="checkbox" checked={fcIds.includes(u.id)}
                          onChange={(e) => setFcIds(e.target.checked ? [...fcIds, u.id] : fcIds.filter(x => x !== u.id))} />
                        {u.name}
                      </label>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="new-outlet-submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create outlet"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <ul className="divide-y divide-slate-100">
        {outlets.length === 0 && <li className="p-6 text-center text-sm text-slate-500">No outlets yet.</li>}
        {outlets.map((o) => (
          <li key={o.id} data-testid={`outlet-row-${o.id}`} className="p-4 flex flex-wrap items-center gap-3 justify-between">
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 text-sm">{o.code} · {o.name}</div>
              {o.address && <div className="text-xs text-slate-500">{o.address}</div>}
              <div className="text-[11px] text-slate-500 mt-0.5">
                FCs: {o.facility_incharge_ids?.length ? o.facility_incharge_ids.map(id => userMap[id]?.name).filter(Boolean).join(", ") : "—"}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" onClick={() => setQrOutlet(o)} data-testid={`qr-outlet-${o.id}`}>
                <QrCode className="w-3.5 h-3.5 mr-1" /> QR
              </Button>
              {canAssign && (
                <Button variant="outline" size="sm" onClick={() => openEdit(o)} data-testid={`edit-outlet-${o.id}`}>
                  <UserCog className="w-3.5 h-3.5 mr-1" /> Allot FCs
                </Button>
              )}
              {canAdd && (
                <Button variant="ghost" size="sm" onClick={() => remove(o)} data-testid={`del-outlet-${o.id}`} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Allot Facility Incharges</DialogTitle>
          </DialogHeader>          {editing && (
            <form onSubmit={saveEdit} className="space-y-3">
              <div className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">{editing.code} · {editing.name}</span>
                <div className="text-xs text-slate-500 mt-0.5">Pick the FCs responsible for this outlet.</div>
              </div>
              <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1">
                {facilityIncharges.length === 0 && <div className="text-xs text-slate-500 p-2">No Facility Incharges yet.</div>}
                {facilityIncharges.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 py-1.5 px-2 text-sm text-slate-700 hover:bg-slate-50 rounded-md cursor-pointer">
                    <input type="checkbox" data-testid={`edit-fc-check-${u.id}`} checked={editFcIds.includes(u.id)}
                      onChange={(e) => setEditFcIds(e.target.checked ? [...editFcIds, u.id] : editFcIds.filter(x => x !== u.id))} />
                    <span className="flex-1">{u.name}</span>
                    <span className="text-[10px] text-slate-500">{u.email}</span>
                  </label>
                ))}
              </div>
              <DialogFooter>
                <Button type="submit" disabled={savingEdit} data-testid="edit-outlet-save" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save assignments"}
                </Button>
              </DialogFooter>
            </form>


          )}
        </DialogContent>
      </Dialog>

      <OutletQRDialog outlet={qrOutlet} open={!!qrOutlet} onOpenChange={(v) => { if (!v) setQrOutlet(null); }} />

    </div>
  );
}

function ZonesCategoriesPanel({ outlets }) {
  const [zones, setZones] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [zoneForm, setZoneForm] = useState({ id: null, name: "", code: "", outlet_id: "" });
  const [zoneSaving, setZoneSaving] = useState(false);
  const [catForm, setCatForm] = useState({ id: null, name: "", zone_ids: [] });
  const [catSaving, setCatSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [z, c] = await Promise.all([api.get("/zones"), api.get("/categories")]);
      setZones(z.data); setCategories(c.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const zoneLabel = (id) => zones.find((z) => z.id === id)?.name || id;
  const outletLabel = (id) => outlets.find((o) => o.id === id)?.code || "";

  const saveZone = async (e) => {
    e.preventDefault();
    if (!zoneForm.name.trim()) return toast.error("Name required");
    setZoneSaving(true);
    try {
      const payload = { name: zoneForm.name.trim(), code: zoneForm.code.trim(), outlet_id: zoneForm.outlet_id || null };
      if (zoneForm.id) {
        await api.put(`/zones/${zoneForm.id}`, payload); toast.success("Zone updated");
      } else {
        await api.post("/zones", payload); toast.success("Zone added");
      }
      setZoneForm({ id: null, name: "", code: "", outlet_id: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setZoneSaving(false); }
  };

  const editZone = (z) => setZoneForm({ id: z.id, name: z.name, code: z.code || "", outlet_id: z.outlet_id || "" });
  const removeZone = async (z) => {
    if (!confirm(`Delete zone "${z.name}"?`)) return;
    try { await api.delete(`/zones/${z.id}`); toast.success("Zone deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const toggleCatZone = (zid) => {
    setCatForm((prev) => ({
      ...prev,
      zone_ids: prev.zone_ids.includes(zid) ? prev.zone_ids.filter((x) => x !== zid) : [...prev.zone_ids, zid],
    }));
  };
  const saveCategory = async (e) => {
    e.preventDefault();
    if (!catForm.name.trim()) return toast.error("Name required");
    setCatSaving(true);
    try {
      const payload = { name: catForm.name.trim(), zone_ids: catForm.zone_ids };
      if (catForm.id) {
        const { data } = await api.put(`/categories/${catForm.id}`, payload);
        toast.success(`Category updated · ${data.resynced_items || 0} item(s) resynced`);
      } else {
        await api.post("/categories", payload); toast.success("Category added");
      }
      setCatForm({ id: null, name: "", zone_ids: [] });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setCatSaving(false); }
  };

  const editCategory = (c) => setCatForm({ id: c.id, name: c.name, zone_ids: c.zone_ids || [] });
  const removeCategory = async (c) => {
    if (!confirm(`Delete category "${c.name}"?`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success("Category deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="text-center py-10 text-slate-400"><Loader2 className="w-4 h-4 mx-auto animate-spin" /></div>;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {/* Zones */}
      <div className="rounded-2xl bg-white border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Layers className="w-4 h-4 text-indigo-600" />
          <div className="font-semibold text-slate-900 text-sm">Zones</div>
          <span className="text-[11px] text-slate-500 ml-auto">{zones.length} total</span>
        </div>

        <form onSubmit={saveZone} className="space-y-2 mb-4 bg-slate-50 rounded-lg p-3">
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="zone-form-name" placeholder="Zone name (e.g. Kitchen)" value={zoneForm.name} onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })} className="h-9" />
            <Input data-testid="zone-form-code" placeholder="Short code (KIT)" value={zoneForm.code} onChange={(e) => setZoneForm({ ...zoneForm, code: e.target.value })} className="h-9" />
          </div>
          <Select value={zoneForm.outlet_id || "_"} onValueChange={(v) => setZoneForm({ ...zoneForm, outlet_id: v === "_" ? "" : v })}>
            <SelectTrigger data-testid="zone-form-outlet" className="h-9"><SelectValue placeholder="Outlet scope" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_">Global (all outlets)</SelectItem>
              {outlets.filter((o) => !o.is_internal).map((o) => <SelectItem key={o.id} value={o.id}>{o.code} · {o.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-2 justify-end">
            {zoneForm.id && <Button type="button" variant="outline" onClick={() => setZoneForm({ id: null, name: "", code: "", outlet_id: "" })}>Cancel</Button>}
            <Button type="submit" data-testid="zone-form-submit" disabled={zoneSaving} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9">
              {zoneSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (zoneForm.id ? "Save" : <><Plus className="w-3.5 h-3.5 mr-1" /> Add zone</>)}
            </Button>
          </div>
        </form>

        <div className="space-y-1.5 max-h-96 overflow-y-auto">
          {zones.length === 0 && <div className="text-xs text-slate-500 py-6 text-center">No zones yet.</div>}
          {zones.map((z) => (
            <div key={z.id} data-testid={`zone-row-${z.id}`} className="flex items-center gap-2 rounded-md border border-slate-100 p-2 text-xs">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800">{z.name} {z.code && <span className="text-slate-400 font-normal">· {z.code}</span>}</div>
                <div className="text-[10px] text-slate-500">{z.outlet_id ? `Outlet: ${outletLabel(z.outlet_id) || "—"}` : "Global"}</div>
              </div>
              <button data-testid={`zone-edit-${z.id}`} onClick={() => editZone(z)} className="p-1 rounded text-slate-500 hover:bg-slate-100"><Pencil className="w-3 h-3" /></button>
              <button data-testid={`zone-delete-${z.id}`} onClick={() => removeZone(z)} className="p-1 rounded text-rose-600 hover:bg-rose-50"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Categories */}
      <div className="rounded-2xl bg-white border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Tag className="w-4 h-4 text-indigo-600" />
          <div className="font-semibold text-slate-900 text-sm">Categories</div>
          <span className="text-[11px] text-slate-500 ml-auto">{categories.length} total</span>
        </div>

        <form onSubmit={saveCategory} className="space-y-2 mb-4 bg-slate-50 rounded-lg p-3">
          <Input data-testid="cat-form-name" placeholder="Category name (e.g. Beverages)" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} className="h-9" />
          <div>
            <div className="text-[11px] font-semibold text-slate-600 mb-1">Zones this category spans</div>
            <div className="flex flex-wrap gap-1.5">
              {zones.length === 0 && <div className="text-[11px] text-slate-500">Add a zone first.</div>}
              {zones.map((z) => (
                <button
                  key={z.id}
                  type="button"
                  data-testid={`cat-zone-toggle-${z.id}`}
                  onClick={() => toggleCatZone(z.id)}
                  className={`px-2 py-1 rounded border text-[11px] font-semibold ${catForm.zone_ids.includes(z.id) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-200"}`}
                >
                  {z.name}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            {catForm.id && <Button type="button" variant="outline" onClick={() => setCatForm({ id: null, name: "", zone_ids: [] })}>Cancel</Button>}
            <Button type="submit" data-testid="cat-form-submit" disabled={catSaving} className="bg-indigo-600 hover:bg-indigo-700 text-white h-9">
              {catSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (catForm.id ? "Save" : <><Plus className="w-3.5 h-3.5 mr-1" /> Add category</>)}
            </Button>
          </div>
        </form>

        <div className="space-y-1.5 max-h-96 overflow-y-auto">
          {categories.length === 0 && <div className="text-xs text-slate-500 py-6 text-center">No categories yet.</div>}
          {categories.map((c) => (
            <div key={c.id} data-testid={`cat-row-${c.id}`} className="flex items-start gap-2 rounded-md border border-slate-100 p-2 text-xs">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800">{c.name}</div>
                <div className="text-[10px] text-slate-500 flex flex-wrap gap-1 mt-0.5">
                  {(c.zone_ids || []).length === 0 ? <span className="italic">No zones</span> : (c.zone_ids || []).map((zid) => (
                    <span key={zid} className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-semibold">{zoneLabel(zid)}</span>
                  ))}
                </div>
              </div>
              <button data-testid={`cat-edit-${c.id}`} onClick={() => editCategory(c)} className="p-1 rounded text-slate-500 hover:bg-slate-100"><Pencil className="w-3 h-3" /></button>
              <button data-testid={`cat-delete-${c.id}`} onClick={() => removeCategory(c)} className="p-1 rounded text-rose-600 hover:bg-rose-50"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
